import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  SxProps,
  TextareaAutosize,
  Theme,
  Typography,
} from '@mui/material';

import clsx from 'clsx';

import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import mixinSx from '@watershed/style/mixinSx';
import { getPaletteUtils } from '@watershed/style/styleUtils';

import CircularProgress from '../CircularProgress';
import CustomReactMarkdown from '../CustomReactMarkdown';
import TextLink from '../TextLink';

export type MarkdownTextFieldWithPreviewProps = {
  id?: string;
  name?: string;
  // Make this flexible because sometimes this is used with a Formik field and
  // the initial value is undefined, and typing this as `string` was not
  // catching undefined values when used as `<Field
  // as={MarkdownTextFieldWithPreview} />`.
  value?: string;
  onChange: (
    evt: React.ChangeEvent<HTMLTextAreaElement>,
    value: string
  ) => void;
  onDidTogglePreview?: (isPreviewing: boolean) => void;
  minRows?: number;
  minHeight?: number;
  maxLength?: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: LocalizedString;
  /**
   * Allows controlling the preview state from outside the component.
   */
  isPreviewing?: boolean;
  defaultToPreview?: boolean;
  placeholder?: LocalizedString;
  previewBlankslate?: LocalizedString;
  previewBackground?: 'background.paper' | 'secondary.light';
  fsUnmask?: boolean;
  isLoading?: boolean;
  autoFocus?: boolean;
  previewSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
} & Omit<
  React.ComponentProps<typeof TextareaAutosize>,
  'onChange' | 'onKeyDown' | 'onKeyUp'
>;

const PREVIEW_CARD_CLASSNAME = 'RFMarkdownTextFieldWithPreview-previewer';
const SWITCH_HEIGHT = 20;

const inputSx: SxProps<Theme> = (theme) => {
  const paletteUtils = getPaletteUtils(theme.palette);
  const { textFieldStyles } = paletteUtils;
  return {
    position: 'relative',
    width: '100%',
    '& textarea': {
      ...textFieldStyles,
      width: '100%',
      resize: 'vertical',
      marginBlockEnd: 0,
      maxHeight: '50svb', // svb = smallest vertical viewport size, 50% of the vertical viewport
      overflowY: 'auto !important',
      '&[aria-invalid="true"]': {
        boxShadow: paletteUtils.boxShadowField.error,
      },
    },
    [`& .${PREVIEW_CARD_CLASSNAME}`]: {
      boxShadow: textFieldStyles.boxShadow,
      padding: textFieldStyles.padding,
      lineHeight: textFieldStyles.lineHeight,
      borderRadius: textFieldStyles.borderRadius,
      maxHeight: '50svb',
      overflowY: 'auto',
    },
  };
};

function OffsetLoading() {
  return (
    <Box
      sx={{
        alignSelf: 'flex-end',
        marginBlockStart: `-${SWITCH_HEIGHT}px`,
        lineHeight: 0,
      }}
    >
      <CircularProgress size={SWITCH_HEIGHT} color="primary" />
    </Box>
  );
}

function OffsetPreviewSwitch({
  isPreviewing,
  onToggle,
}: {
  isPreviewing: boolean;
  onToggle: () => void;
}) {
  return (
    <FormControlLabel
      label={
        <Typography
          component="span"
          variant="body3"
          sx={{ color: (theme) => theme.palette.text.secondary }}
        >
          <Trans context="Preview text markdown">
            Preview{' '}
            <TextLink
              href="https://www.markdownguide.org/basic-syntax/"
              target="_blank"
              rel="noreferrer"
              underline="always"
              sx={{
                color: 'inherit',
                textDecorationColor: 'currentColor',
              }}
            >
              Markdown
            </TextLink>
          </Trans>
        </Typography>
      }
      control={
        <Switch
          data-testid={TestIds.MarkdownTextFieldWithPreviewToggle}
          onClick={onToggle}
          size="small"
          checked={isPreviewing}
        />
      }
      sx={{
        alignSelf: 'flex-end',
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
        gap: 1,
        marginBlockStart: `-${SWITCH_HEIGHT}px`,
      }}
    />
  );
}

function Preview({
  value,
  previewBlankslate,
  previewBackground,
  minHeight,
  previewSx,
  onDoubleClick,
}: Required<
  Pick<
    MarkdownTextFieldWithPreviewProps,
    'value' | 'previewBlankslate' | 'previewBackground' | 'minHeight'
  >
> &
  Pick<MarkdownTextFieldWithPreviewProps, 'previewSx'> & {
    onDoubleClick?: () => void;
  }) {
  const hasPreviewContent = value.length > 0;

  return (
    <Box
      className={PREVIEW_CARD_CLASSNAME}
      data-testid={TestIds.MarkdownTextFieldWithPreviewDisplay}
      onDoubleClick={onDoubleClick}
      sx={mixinSx(
        {
          backgroundColor: previewBackground,
          minHeight,
        },
        previewSx
      )}
    >
      {hasPreviewContent ? (
        <CustomReactMarkdown source={value} />
      ) : (
        <Typography variant="body2">{previewBlankslate}</Typography>
      )}
    </Box>
  );
}

export default function MarkdownTextFieldWithPreview({
  id,
  name,
  value = '',
  onChange,
  onDidTogglePreview,
  minRows = 3,
  minHeight = 80,
  maxLength,
  disabled = false,
  error = false,
  helperText,
  isPreviewing: isPreviewingInitial,
  defaultToPreview = false,
  placeholder,
  previewBlankslate = t`No text here yet`,
  previewBackground = 'background.paper',
  isLoading = false,
  autoFocus = false,
  fsUnmask = false,
  previewSx,
  sx,
  onKeyDown,
  onKeyUp,
  ...textareaProps
}: MarkdownTextFieldWithPreviewProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(
    isPreviewingInitial ?? defaultToPreview
  );

  // Allows outside state to control preview state if needed
  useEffect(() => {
    if (
      isPreviewingInitial !== undefined &&
      isPreviewing !== isPreviewingInitial
    ) {
      setIsPreviewing(isPreviewingInitial);
      onDidTogglePreview?.(isPreviewingInitial);
    }
  }, [isPreviewingInitial, isPreviewing, onDidTogglePreview]);

  const togglePreview = () => {
    setIsPreviewing((value) => !value);
    onDidTogglePreview?.(!isPreviewing);
    if (!isPreviewing) {
      textareaRef.current?.focus();
    }
  };

  const shouldShowPreviewSwitch =
    !isLoading && !disabled && isPreviewingInitial === undefined;

  return (
    <Stack
      gap={1}
      sx={mixinSx(
        inputSx,
        { marginBlockStart: -1 }, // Undo gap
        sx
      )}
    >
      {isLoading ? (
        <OffsetLoading />
      ) : (
        shouldShowPreviewSwitch && (
          <OffsetPreviewSwitch
            isPreviewing={isPreviewing}
            onToggle={togglePreview}
          />
        )
      )}
      {isPreviewing ? (
        <Preview
          value={value}
          previewBlankslate={previewBlankslate}
          previewBackground={previewBackground}
          minHeight={minHeight}
          previewSx={previewSx}
          onDoubleClick={shouldShowPreviewSwitch ? togglePreview : undefined}
        />
      ) : (
        <TextareaAutosize
          data-testid={TestIds.MarkdownTextFieldWithPreviewTextArea}
          autoFocus={autoFocus}
          ref={textareaRef}
          id={id}
          name={name}
          value={value}
          minRows={minRows}
          maxLength={maxLength}
          aria-invalid={error}
          disabled={disabled}
          placeholder={placeholder ?? previewBlankslate}
          onChange={(evt: React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange(evt, evt.currentTarget.value)
          }
          className={clsx(textareaProps.className, fsUnmask && 'fs-unmask')}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          {...textareaProps}
        />
      )}
      {helperText && (
        <Typography
          role="alert"
          sx={{ color: (theme) => theme.palette.error.dark }}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}
